import React, { useState } from 'react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';
import { Dropdown } from 'js/design_system/forms/Dropdown';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import FileUpload from 'js/design_system/FileUpload';
import { currentUser } from 'js/utils/user';
import { createReportFromUpload } from '../api';
import { captureException } from '@sentry/browser';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { trackClick } from 'js/utils/trackClick';
import routes from 'js/core_pre_webpack/routes';
import type { DropFilesEventHandler } from 'react-dropzone';

interface WorkAttachment {
  id: number;
  filename: string | null;
}

interface UserWork {
  id: number;
  title: string;
  attachments_attributes?: WorkAttachment[];
}

const PrivacyContainer = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 8px;
  gap: 8px;
  max-width: 520px;
`;

const PrivacyText = styled(Body).attrs({ size: 'md' })`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PrivacyGuarantee = () => (
  <PrivacyContainer>
    <Icon name="lock" size={44} style={{ color: '#6d80e1' }} />
    <PrivacyText>
      <b>Academia Privacy Guarantee</b>
      Your document is private. It will remain private. CiteRight will not share
      your document with anyone.
    </PrivacyText>
  </PrivacyContainer>
);

const WorkSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;
  max-width: 600px;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: var(--spacing-md-24, 24px) 0;

  &:before,
  &:after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border-light, #e5e7eb);
  }

  &:before {
    margin-right: var(--spacing-xs-16, 16px);
  }

  &:after {
    margin-left: var(--spacing-xs-16, 16px);
  }
`;

const OrText = styled(Body).attrs({
  size: 'sm',
})`
  color: var(--text-secondary, #6b7280);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md-40, 40px);
  width: 100%;
`;

const ErrorMessage = styled.div`
  background: var(--brand-colors-error-100);
  border: 1px solid var(--brand-colors-error-500);
  border-radius: var(--border-radius-radius-md);
  padding: var(--spacing-xs-16, 16px);
  color: var(--brand-colors-error-900);
  font-size: var(--type-body-sm-size, 14px);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  max-width: 600px;
  width: 100%;
`;

interface WorkSelectorUploadProps {
  userWorks?: UserWork[];
  showPrivacyGuarantee?: boolean;
  showUserWorks?: boolean;
  importSource?: string;
  onAnalyzeWork?: (workId: number) => void;
  onReportCreated?: (reportId: string) => void;
}

export const WorkSelectorUpload: React.FC<WorkSelectorUploadProps> = ({
  userWorks: userWorksProp,
  showPrivacyGuarantee = true,
  showUserWorks = true,
  onAnalyzeWork,
  onReportCreated,
}) => {
  // Use null coalescing to handle both null and undefined from Rails
  const userWorks = userWorksProp ?? [];
  const [selectedWork, setSelectedWork] = useState<UserWork | undefined>(
    userWorks.length > 0 ? userWorks[0] : undefined,
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyzeSelectedWork = async () => {
    if (!selectedWork) {
      setError('Please select a work to analyze');
      return;
    }

    trackClick({
      source: 'citeright.import_click',
      extra: {
        work_id: selectedWork.id,
        source: 'landing',
      },
    });

    setIsProcessing(true);

    // Use callback if provided (avoids page reload), otherwise redirect
    if (onAnalyzeWork) {
      onAnalyzeWork(selectedWork.id);
    } else {
      window.location.href = `/citeright?ui=inline&work_id=${selectedWork.id}`;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF or DOCX file');
      return;
    }

    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size must be less than 50MB');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await createReportFromUpload(file, 'landing');

      if (response.report && response.report.report_id) {
        // Use callback if provided (avoids page reload), otherwise redirect
        if (onReportCreated) {
          onReportCreated(response.report.report_id.toString());
        } else {
          window.location.href = `/citeright?ui=inline&report_id=${response.report.report_id}`;
        }
      } else {
        setError('Failed to create report. Please try again.');
      }
    } catch (err) {
      captureException(err, {
        tags: { component: 'WorkSelectorUpload', action: 'file_upload' },
      });
      setError(
        'A problem occurred while uploading the document, please try again.',
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDroppedFiles: DropFilesEventHandler = (
    acceptedFiles,
    rejectedFiles,
  ) => {
    if (!currentUser) {
      window.location.href = routes.login_path({
        post_login_redirect_url: '/citeright',
      });
      return;
    }

    if (rejectedFiles.length > 0) {
      // Rejected files are due to file extension mismatch
      // The FileUpload component validates based on acceptedFileExtensions
      // Size validation happens later in handleFileChange
      setError('Please upload a PDF or DOCX file');
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      // Create a synthetic event to pass to handleFileChange
      const syntheticEvent = {
        target: { files: [file] } as unknown,
      } as React.ChangeEvent<HTMLInputElement>;
      handleFileChange(syntheticEvent);
    }
  };

  const worksWithAttachments = userWorks.filter(
    (work) =>
      work?.attachments_attributes && work.attachments_attributes.length > 0,
  );

  // Shared FileUpload component
  const fileUploadComponent = (isPrimary: boolean) => (
    <ImpressionWatcher source="citeright.upload_file_button">
      <FileUpload
        onNewFiles={handleDroppedFiles}
        acceptedFileExtensions={['.pdf', '.docx']}
        multiple={false}
        description="your DOCX or PDF"
        note="Maximum file size: 50MB"
        primary={isPrimary}
        secondary={!isPrimary}
        disabled={isProcessing}
        style={{ width: '100%', maxWidth: '600px' }}
        buttonText="Choose a draft"
      />
    </ImpressionWatcher>
  );

  return (
    <Container>
      {error && (
        <ErrorMessage>
          <Icon name="error" size={20} />
          {error}
        </ErrorMessage>
      )}
      <WorkSelectorContainer>
        {showUserWorks && worksWithAttachments.length > 0 ? (
          // Upload drag-drop first (primary), then work selector (secondary)
          <>
            {fileUploadComponent(true)}
            <OrDivider>
              <OrText>or</OrText>
            </OrDivider>
            <ImpressionWatcher source="citeright.document_dropdown_select">
              <Dropdown
                label="Find reference suggestions for your recent work:"
                data-test="document-dropdown-select"
                options={worksWithAttachments}
                value={selectedWork}
                keyExtractor={(work) => work.id.toString()}
                renderOption={(work) => work.title || 'Untitled'}
                renderValue={(work) => work?.title || 'Untitled'}
                onChange={setSelectedWork}
                placeholder="Select a document from your library"
                disabled={isProcessing}
              />
            </ImpressionWatcher>
            <ImpressionWatcher source="citeright.analyze_selected_work_button">
              <Button
                secondary
                size="lg"
                onClick={handleAnalyzeSelectedWork}
                disabled={isProcessing || !selectedWork}
                data-test="analyze-selected-work-button"
                style={{ width: '100%' }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '80%',
                    display: 'inline-block',
                  }}
                >
                  {isProcessing
                    ? 'Processing...'
                    : selectedWork
                      ? `Analyze "${selectedWork.title || 'Untitled'}"`
                      : 'Select a document to analyze'}
                </span>
              </Button>
            </ImpressionWatcher>
          </>
        ) : (
          // No works with attachments: just show upload drag-and-drop
          fileUploadComponent(true)
        )}
      </WorkSelectorContainer>

      {showPrivacyGuarantee && <PrivacyGuarantee />}
    </Container>
  );
};

export default WorkSelectorUpload;
